/**
 * Calculate the great-circle distance between two GPS coordinates using the Haversine formula.
 *
 * The Haversine formula determines the shortest distance over the earth's surface
 * between two points given their latitude and longitude. It accounts for the earth's
 * curvature using a spherical model (radius = 6371 km).
 *
 * Used by Sideways view to convert GPS coordinates into pixel positions on the
 * side-view image by measuring distances from reference points.
 *
 * Formula: d = R * 2 * atan2(√a, √(1-a))
 *   where a = sin²(Δφ/2) + cos(φ1) * cos(φ2) * sin²(Δλ/2)
 *
 * @param lat1 - Latitude of first point in degrees
 * @param lon1 - Longitude of first point in degrees
 * @param lat2 - Latitude of second point in degrees
 * @param lon2 - Longitude of second point in degrees
 * @returns Distance in metres between the two points
 *
 * @example
 * // Distance between two reference points on a vessel
 * const length = calculateDistance(55.716, 13.224, 55.718, 13.228);
 * // Returns ~350 (metres)
 */
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371e3; // Earth radius in metres
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180; // Latitude difference in radians
  const Δλ = ((lon2 - lon1) * Math.PI) / 180; // Longitude difference in radians

  // Haversine of the central angle
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in metres
};
